import {
  getCountries,
  parsePhoneNumberWithError,
  isValidPhoneNumber,
  getCountryCallingCode,
  type CountryCode,
} from "libphonenumber-js";

import { getCountryName } from "../../l10n/Country";

type ParsedPhoneNumberResult = {
  countryCallingCode: string;
  nationalNumber: string;
  isValid: boolean;
};

export type CountryCallingCodeOption = {
  code: CountryCode;
  name: string;
  flag: string;
  callingCode: string;
  label: string;
  value: string;
};

const defaultParsedPhoneNumber: ParsedPhoneNumberResult = {
  countryCallingCode: "",
  nationalNumber: "",
  isValid: false,
};

export type { CountryCode };

export function parseInternationalPhoneNumber(
  phoneString: string,
): ParsedPhoneNumberResult {
  if (!phoneString?.trim()) {
    return defaultParsedPhoneNumber;
  }
  let phoneNumber;
  let isValid;
  try {
    phoneNumber = parsePhoneNumberWithError(phoneString);
    isValid = phoneNumber.isValid();
  } catch {
    return defaultParsedPhoneNumber;
  }
  return {
    countryCallingCode: phoneNumber.countryCallingCode
      ? `+${phoneNumber.countryCallingCode}`
      : "",
    nationalNumber: phoneNumber.nationalNumber || "",
    isValid: isValid || false,
  };
}

export function getCountryForInternationalPhoneNumber(
  phoneString: string,
): CountryCode | undefined {
  if (!phoneString?.trim()) {
    return undefined;
  }
  try {
    return parsePhoneNumberWithError(phoneString).country;
  } catch {
    return undefined;
  }
}

export function validatePhoneNumber(phoneString: string): boolean {
  try {
    return phoneString?.trim() ? isValidPhoneNumber(phoneString) : false;
  } catch {
    return false;
  }
}

export function getCallingCodeForCountry(countryCode: CountryCode): string {
  try {
    return countryCode?.trim() ? getCountryCallingCode(countryCode) : "";
  } catch {
    return "";
  }
}

// region codes that do not have a flag emoji and should instead use the globe emoji
const DEPRECATED_REGIONS = new Set([
  "AN", // AN (used for Netherlands Antilles / ANG currency)
  "XA", // XA (used for regions with XAF currency)
  "XC", // XC (used for regions with XCD currency)
  "XO", // XO (used for regions with XOF currency)
  "XP", // XP (used for regions with XPF currency)
]);

export const countryCodeToFlag = (countryCode: string) => {
  // This is the mapping between the alpha2 country code and the utf8 flag
  if (DEPRECATED_REGIONS.has(countryCode)) {
    return "🌐";
  }
  return Array.from(countryCode.substring(0, 2))
    .map((letter) => String.fromCodePoint(letter.charCodeAt(0) + 127397))
    .join("");
};

export function getCountryCallingCodeOptions(
  selectedLocale = "en",
): CountryCallingCodeOption[] {
  return getCountries()
    .map((code) => {
      const callingCode = `+${getCallingCodeForCountry(code)}`;
      const name = getCountryName(code, selectedLocale) || code;
      const flag = countryCodeToFlag(code);

      return {
        code,
        name,
        flag,
        callingCode,
        label: `${flag} ${name} (${callingCode})`,
        value: code,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, selectedLocale));
}
